import { createMachine, state, transition, guard } from 'robot3';

// Guard functions for state machine conditions
export const guards = {
  // Enemy detection
  hasEnemyInRange: (context) => {
    const { base, ai } = context;
    return ai && ai.nearbyEnemies && ai.nearbyEnemies.length > 0;
  },
  
  // Combat capability
  canFireMissile: (context) => {
    const { base, ai } = context;
    const now = Date.now();
    return base && ai &&
           base.ammo >= 5 && // BASE_CONFIG.MISSILE_AMMO_COST
           ai.currentTarget &&
           (now - ai.lastFireTime) >= base.fireRate;
  },
  
  // Resource needs
  needsAmmo: (context) => {
    const { base } = context;
    return base && base.ammo < base.maxAmmo * 0.3;
  },
  
  criticalAmmo: (context) => {
    const { base } = context;
    return base && base.ammo < base.maxAmmo * 0.1;
  },
  
  needsMetal: (context) => {
    const { base } = context;
    return base && base.resources.metal < base.maxMetal * 0.5 && !guards.criticalAmmo(context);
  },
  
  needsOrganics: (context) => {
    const { base } = context;
    // Need organics if we have less than 4 units worth (40 organics)
    // Increased threshold so bases request organics before critical
    return base && base.resources.organics < 40 && !guards.criticalAmmo(context);
  },
  
  // Health status
  healthCritical: (context) => {
    const { base } = context;
    return base && base.health < base.maxHealth * 0.5;
  },
  
  healthFull: (context) => {
    const { base } = context;
    return base && base.health >= base.maxHealth;
  },
  
  // Spawning capability
  canSpawnUnit: (context) => {
    const { base } = context;
    return base && (
      base.resources.organics >= 25 || // RESOURCE_CONFIG.ORGANICS_PER_UNIT
      base.crystalUnitsToSpawn > 0
    );
  },
  
  // Repair status
  isBeingRepaired: (context) => {
    const { base, ai } = context;
    // Check if a max-level ally is near and repairing
    return ai && ai.nearbyAllies && ai.nearbyAllies.some(ally => 
      ally.level >= 15 && // GAME_CONFIG.MAX_LEVEL
      ally.state === 'repairing_base' &&
      base.getDistance(ally) < 50
    );
  },
  
  // Target validity
  hasValidTarget: (context) => {
    const { ai } = context;
    return ai && ai.currentTarget && ai.isValidTarget(ai.currentTarget);
  },
  
  // Resource visibility
  canSeeMetalResource: (context) => {
    const { ai } = context;
    return ai && ai.nearbyResources && ai.nearbyResources.some(r => r.type === 'metal');
  },
  
  canSeeNebulaWithSlots: (context) => {
    const { ai } = context;
    return ai && ai.nearbyResources && ai.nearbyResources.some(r => 
      r.type === 'nebula' && 
      r.active && 
      r.gatherers && 
      r.gatherers.size < 5 // RESOURCE_CONFIG.NEBULA_MAX_GATHERERS
    );
  }
};

// Events that can trigger state transitions
export const events = {
  // Initialization
  INITIALIZED: 'INITIALIZED',
  
  // Combat events
  ENEMY_DETECTED: 'ENEMY_DETECTED',
  TARGET_LOST: 'TARGET_LOST',
  MISSILE_FIRED: 'MISSILE_FIRED',
  
  // Resource events
  LOW_AMMO: 'LOW_AMMO',
  CRITICAL_AMMO: 'CRITICAL_AMMO',
  LOW_METAL: 'LOW_METAL',
  LOW_ORGANICS: 'LOW_ORGANICS',
  RESOURCE_RECEIVED: 'RESOURCE_RECEIVED',
  AMMO_DONATED: 'AMMO_DONATED',
  
  // Health events
  DAMAGE_TAKEN: 'DAMAGE_TAKEN',
  HEALTH_CRITICAL: 'HEALTH_CRITICAL',
  REPAIR_STARTED: 'REPAIR_STARTED',
  REPAIR_COMPLETE: 'REPAIR_COMPLETE',
  
  // Spawning events
  SPAWN_READY: 'SPAWN_READY',
  UNIT_SPAWNED: 'UNIT_SPAWNED',
  
  // Destruction
  DESTROYED: 'DESTROYED',
  
  // Periodic check
  CHECK_CONDITIONS: 'CHECK_CONDITIONS'
};

// Create the team base state machine
export const createTeamBaseStateMachine = () => {
  return createMachine('idle', {
    // Initial state - just created
    idle: state(
      transition(events.INITIALIZED, 'operating'),
      transition(events.ENEMY_DETECTED, 'defending'),
      transition(events.DESTROYED, 'destroyed')
    ),
    
    // Normal operation - spawning units and managing resources
    operating: state(
      // Resource management
      transition(events.CRITICAL_AMMO, 'requesting_ammo'),
      transition(events.LOW_AMMO, 'requesting_metal', guard(guards.canSeeMetalResource)),
      transition(events.LOW_METAL, 'requesting_metal'),
      transition(events.LOW_ORGANICS, 'requesting_organics', guard(guards.canSeeNebulaWithSlots)),
      
      // Health management - only defend when actually damaged
      transition(events.HEALTH_CRITICAL, 'defending'),
      transition(events.REPAIR_STARTED, 'repairing'),
      
      // Destruction
      transition(events.DESTROYED, 'destroyed')
    ),
    
    // Actively defending against enemies
    defending: state(
      // Return to normal when health recovers above 70%
      transition(events.CHECK_CONDITIONS, 'operating', guard((ctx) => {
        const { base } = ctx;
        return base && base.health >= base.maxHealth * 0.7;
      })),
      
      // Continue defending while enemies present
      transition(events.TARGET_LOST, 'operating', guard((ctx) => !guards.hasEnemyInRange(ctx))),
      
      // Resource crisis overrides defense
      transition(events.CRITICAL_AMMO, 'requesting_ammo', guard((ctx) => !guards.hasValidTarget(ctx))),
      
      // Repair if offered
      transition(events.REPAIR_STARTED, 'repairing'),
      
      // Destruction
      transition(events.DESTROYED, 'destroyed')
    ),
    
    // Low on ammo, need metal resources
    requesting_metal: state(
      // Upgrade to critical if ammo gets very low
      transition(events.CRITICAL_AMMO, 'requesting_ammo'),
      
      // Return to normal when resources adequate
      transition(events.RESOURCE_RECEIVED, 'operating', guard((ctx) => !guards.needsAmmo(ctx))),
      transition(events.CHECK_CONDITIONS, 'operating', guard((ctx) => !guards.needsAmmo(ctx))),
      
      // Health management - only defend when actually damaged
      transition(events.HEALTH_CRITICAL, 'defending'),
      transition(events.REPAIR_STARTED, 'repairing'),
      
      // Destruction
      transition(events.DESTROYED, 'destroyed')
    ),
    
    // Critical ammo - accepting soldier donations
    requesting_ammo: state(
      // Return to normal operations when ammo restored
      transition(events.AMMO_DONATED, 'operating', guard((ctx) => !guards.needsAmmo(ctx))),
      transition(events.RESOURCE_RECEIVED, 'operating', guard((ctx) => !guards.needsAmmo(ctx))),
      
      // Downgrade to metal request if situation improves
      transition(events.CHECK_CONDITIONS, 'requesting_metal', guard((ctx) => !guards.criticalAmmo(ctx) && guards.needsAmmo(ctx))),
      
      // Health management - only defend when actually damaged
      transition(events.HEALTH_CRITICAL, 'defending'),
      transition(events.REPAIR_STARTED, 'repairing'),
      
      // Destruction
      transition(events.DESTROYED, 'destroyed')
    ),
    
    // Being repaired by a max-level soldier
    repairing: state(
      // Continue repairs unless interrupted by combat
      transition(events.ENEMY_DETECTED, 'defending', guard((ctx) => guards.hasEnemyInRange(ctx) && guards.canFireMissile(ctx))),
      
      // Repair complete
      transition(events.REPAIR_COMPLETE, 'operating'),
      transition(events.CHECK_CONDITIONS, 'operating', guard((ctx) => guards.healthFull(ctx) || !guards.isBeingRepaired(ctx))),
      
      // Destruction (repair failed)
      transition(events.DESTROYED, 'destroyed')
    ),
    
    // Requesting organics from nebulas
    requesting_organics: state(
      // Upgrade to critical ammo if needed
      transition(events.CRITICAL_AMMO, 'requesting_ammo'),
      
      // Return to normal when have enough organics
      transition(events.RESOURCE_RECEIVED, 'operating', guard((ctx) => !guards.needsOrganics(ctx))),
      transition(events.CHECK_CONDITIONS, 'operating', guard((ctx) => !guards.needsOrganics(ctx) || !guards.canSeeNebulaWithSlots(ctx))),
      
      // Health management - only defend when actually damaged
      transition(events.HEALTH_CRITICAL, 'defending'),
      transition(events.REPAIR_STARTED, 'repairing'),
      
      // Destruction
      transition(events.DESTROYED, 'destroyed')
    ),
    
    // Base has been destroyed
    destroyed: state(
      // Terminal state - can only be revived by max-level soldier
      transition(events.REPAIR_STARTED, 'repairing')
    )
  });
};

// Helper function to create state machine context
export const createContext = (base, ai) => ({
  base,
  ai,
  timestamp: Date.now()
});